import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Lenis from 'lenis';
import Navbar from './components/navbar';
import JourneyLine from './components/JourneyLine';
import Home from './pages/Home';
import { useScrollProgress } from './hooks/useScrollProgress';
import './styles/global.css';

gsap.registerPlugin(ScrollTrigger);

function ScrollProgressBar() {
  const progress = useScrollProgress();
  return (
    <motion.div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        height: '2px',
        background: 'linear-gradient(90deg, #3d5af1, #5b7fff)',
        transformOrigin: '0%',
        scaleX: progress,
        zIndex: 10001,
        pointerEvents: 'none',
      }}
    />
  );
}

export default function App() {
  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
    });

    // Expose globally so the navbar / anchor handlers can use smooth scroll.
    window.__lenis = lenis;

    // Use gsap.ticker for proper ScrollTrigger pin synchronisation.
    const lenisRaf = (time) => lenis.raf(time * 1000);
    gsap.ticker.add(lenisRaf);
    gsap.ticker.lagSmoothing(0);
    lenis.on('scroll', ScrollTrigger.update);
    document.documentElement.style.scrollBehavior = 'auto';

    // Intercept in-page anchor clicks so they scroll smoothly via Lenis
    // (rather than the browser's instant jump).
    const onAnchorClick = (e) => {
      const a = e.target.closest('a[href^="#"]');
      if (!a) return;
      const href = a.getAttribute('href');
      if (!href || href === '#') return;
      const el = document.querySelector(href);
      if (!el) return;
      e.preventDefault();
      lenis.scrollTo(el, { offset: 0, duration: 1.4 });
      window.history.replaceState(null, '', href);
    };
    document.addEventListener('click', onAnchorClick);

    return () => {
      document.removeEventListener('click', onAnchorClick);
      gsap.ticker.remove(lenisRaf);
      delete window.__lenis;
      lenis.destroy();
      document.documentElement.style.scrollBehavior = '';
    };
  }, []);

  return (
    <>
      <ScrollProgressBar />
      <Navbar />
      <JourneyLine />
      <Home />
    </>
  );
}
